import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { RepairerSiteCreate, UserCreate } from '../../../model/user.model';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { COUNTRIES } from '../../../model/states.model';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { SysAdminService } from '../../../services/sys-admin.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EMAIL_PATTERN_STRING } from '../../../consts/patterns.const';
import { getErrorMessage } from '../../../consts/form-field-errors';
import { QUOTING_SYSTEMS } from '../../../consts/quotingSystems.const';
import { abnValidator } from '../../../validators/abn.validator';
import { PhoneValidator } from '../../../validators/phone.validator';
import { DateInputHelper } from '../../../helpers/date-input.helper';
import moment from 'moment/moment';
import { EIO_DATE_BE_FORMAT } from '../../../consts/localization.const';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';

@Component({
  selector: 'app-add-repairer-site',
  templateUrl: './add-repairer-site.component.html',
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    MatButton
  ]
})
export class AddRepairerSiteComponent implements OnInit, OnDestroy {
  repairerForm: UntypedFormGroup;
  countries = COUNTRIES;
  quotingSystemList = QUOTING_SYSTEMS;
  pending = signal<boolean>(false);
  protected readonly getErrorMessage = getErrorMessage;
  private unsubscribe = new Subject<void>();

  data = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<AddRepairerSiteComponent>);
  private fb = inject(UntypedFormBuilder);
  private sysAdminService = inject(SysAdminService);
  private toolbarService = inject(ToolbarService);
  private message = inject(MessagingService);
  private escapeDialogRef = inject(EscapeDialogHelper);
  dateInputHelper = inject(DateInputHelper);

  get username() {
    return this.repairerForm.get('username');
  }
  get emailRepairer() {
    return this.repairerForm.get('emailRepairer');
  }
  get abn() {
    return this.repairerForm.get('abn');
  }
  get companyName() {
    return this.repairerForm.get('companyName');
  }
  get phoneNumber() {
    return this.repairerForm.get('phoneNumber');
  }
  get licenseNumber() {
    return this.repairerForm.get('licenseNumber');
  }
  get emailCompany() {
    return this.repairerForm.get('emailCompany');
  }
  get country() {
    return this.repairerForm.get('country');
  }
  get firstName() {
    return this.repairerForm.get('firstName');
  }
  get lastName() {
    return this.repairerForm.get('lastName');
  }
  get phone() {
    return this.repairerForm.get('phone');
  }
  get quotingSystem() {
    return this.repairerForm.get('quotingSystem');
  }
  get repairerGroupId() {
    return this.repairerForm.get('repairerGroupId');
  }
  get licenseRenewalDate() {
    return this.repairerForm.get('licenseRenewalDate');
  }
  get primaryAccountsEmail() {
    return this.repairerForm.get('primaryAccountsEmail');
  }
  get secondaryAccountsEmail() {
    return this.repairerForm.get('secondaryAccountsEmail');
  }

  ngOnInit() {
    const pendingPublication = this.data?.repairerSite?.mainContact?.isActivated;
    this.repairerForm = this.fb.group({
      companyName: [
        {
          value: this.data?.repairerSite?.companyName ?? '',
          disabled: pendingPublication
        },
        Validators.required
      ],
      emailCompany: [
        this.data?.repairerSite?.email ?? '',
        [Validators.required, Validators.pattern(EMAIL_PATTERN_STRING)]
      ],
      phoneNumber: [this.data?.repairerSite?.phoneNumber ?? '', [Validators.required, PhoneValidator]],
      licenseNumber: [this.data?.repairerSite?.licenseNumber ?? ''],
      abn: [
        {
          value: this.data?.repairerSite?.abn ?? '',
          disabled: pendingPublication
        },
        this.data?.repairerSite?.country === this.countries[0].stateName
          ? [Validators.required, abnValidator]
          : Validators.required
      ],
      country: [
        {
          value: this.data?.repairerSite?.country ?? '',
          disabled: pendingPublication
        },
        Validators.required
      ],
      username: [
        {
          value: this.data?.repairerSite?.mainContact?.username ?? '',
          disabled: this.data?.repairerSite
        },
        Validators.required
      ],
      firstName: [this.data?.repairerSite?.mainContact?.firstName ?? '', Validators.required],
      lastName: [this.data?.repairerSite?.mainContact?.lastName ?? '', Validators.required],
      emailRepairer: [
        {
          value: this.data?.repairerSite?.mainContact?.emailAddress ?? '',
          disabled: pendingPublication
        },
        [Validators.required, Validators.pattern(EMAIL_PATTERN_STRING)]
      ],
      phone: [this.data?.repairerSite?.mainContact?.phone ?? '', PhoneValidator],
      quotingSystem: [this.data?.repairerSite?.quotingSystem ?? ''],
      repairerGroupId: [this.data?.repairerSite?.repairerGroupId],
      licenseRenewalDate: [this.data?.repairerSite?.licenseRenewalDate],
      primaryAccountsEmail: [
        this.data?.repairerSite?.primaryAccountsEmail ?? '',
        [Validators.required, Validators.pattern(EMAIL_PATTERN_STRING)]
      ],
      secondaryAccountsEmail: [
        this.data?.repairerSite?.secondaryAccountsEmail ?? '',
        Validators.pattern(EMAIL_PATTERN_STRING)
      ]
    });

    this.phoneNumber.markAsTouched();
    this.phone.markAsTouched();

    this.escapeDialogRef.escapeDialog(this.dialogRef);
    this.country.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (country: string) => {
        if (country === this.countries[0].stateName) {
          this.abn.setValidators([Validators.required, abnValidator]);
          this.adjustFormatOfAbn();
        } else {
          this.abn.setValidators(Validators.required);
        }
        this.abn.updateValueAndValidity();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  adjustFormatOfAbn() {
    this.abn.updateValueAndValidity();
    if (this.abn.valid && this.country.value === this.countries[0].stateName) {
      this.abn.patchValue(this.abn.value.replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
    }
  }

  private getRepairSiteData(): RepairerSiteCreate {
    return {
      abn: this.abn.value,
      companyName: this.companyName.value,
      phoneNumber: this.phoneNumber.value,
      licenseNumber: this.licenseNumber.value,
      email: this.emailCompany.value,
      primaryAccountsEmail: this.primaryAccountsEmail.value,
      secondaryAccountsEmail: this.secondaryAccountsEmail.value,
      country: this.country.value,
      quotingSystem: this.quotingSystem.value,
      repAdmin: {
        username: this.username.value.trim(),
        firstName: this.firstName.value.trim(),
        lastName: this.lastName.value.trim(),
        email: this.emailRepairer.value.trim(),
        phone: this.phone.value.trim()
      } as UserCreate,
      repairerGroupId: this.repairerGroupId.value,
      licenseRenewalDate: this.licenseRenewalDate.value
        ? moment(this.licenseRenewalDate.value).format(EIO_DATE_BE_FORMAT)
        : null
    };
  }

  addRepairerSite() {
    this.pending.set(true);
    this.toolbarService.setCloudState('SAVING');

    this.sysAdminService.createRepairerSite(this.getRepairSiteData()).subscribe(
      () => this.dialogRef.close('Success'),
      (error) => {
        if (error.error === 'DUPLICATE_USERNAME') {
          this.username.setErrors({ usernameDuplicate: true });
        } else {
          this.message.error('Could not create the repairer site.');
        }
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      () => this.toolbarService.setCloudState('RESTING')
    );
  }

  reinviteRepairerSite() {
    this.pending.set(true);
    this.toolbarService.setCloudState('SAVING');

    const emailRepairer = this.emailRepairer.dirty ? encodeURIComponent(this.emailRepairer.value.trim()) : '';

    this.sysAdminService.updateAndReinviteRepairerSite(emailRepairer, this.data.repairerSite.id).subscribe(
      () => this.dialogRef.close('Success'),
      () => {
        this.message.error('Could not re-invite the repairer site.');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      () => this.toolbarService.setCloudState('RESTING')
    );
  }

  editRepairerSite() {
    this.pending.set(true);
    this.toolbarService.setCloudState('SAVING');
    const repairerSite = this.getRepairSiteData();
    this.sysAdminService.updateUnpublishedRepairerSites(this.data.repairerSite.id, repairerSite).subscribe({
      next: () => {
        this.dialogRef.close('Success');
        this.toolbarService.setCloudState('RESTING');
      },
      error: () => {
        this.message.error('Could not edit the repairer site.');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      }
    });
  }
}
